import React, { useEffect, useState } from 'react'

import { Grid, Theme, useMediaQuery } from '@mui/material'
import { isEmpty, isEqual } from 'lodash'
import { useTranslation } from 'react-i18next'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { fetchDrawdownReport } from '../ReportsSlice'
import { DrawdownChartTypeEnum } from '../types'

import { CommonDrawdownChart } from './CommonDrawdownChart'

export const Drawdown = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const matchesBreakpoints: boolean = useMediaQuery((theme: Theme) => theme.breakpoints.up(2000))
  const { period, currentAccount } = useAppSelector(({ userAccounts }) => userAccounts)
  const drawdown = useAppSelector(({ reports }) => reports.drawdown)
  const {
    averageDrawdown,
    biggestDrawdown,
    averageNumberOfTrades,
    averageNumberOfDays,
    numberOfDays,
    meta,
  } = drawdown
  const [displayWidth, setDisplayWidth] = useState(window.innerWidth)

  useEffect(() => {
    const periodsIsEqual = meta?.period && period && isEqual(meta.period, period)
    const isNeedToUpdate =
      isEmpty(drawdown) || !periodsIsEqual || currentAccount?.id !== meta.account

    if (currentAccount?.id && isNeedToUpdate) {
      dispatch(
        fetchDrawdownReport({
          account: currentAccount?.id,
          period: period,
        }),
      )
    }
  }, [period, currentAccount])

  useEffect(() => {
    const handleResize = () => {
      setDisplayWidth(window.innerWidth)
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <Grid container spacing={2}>
      <Grid
        item
        lg={matchesBreakpoints ? 4 : 6}
        xs={12}
        sm={12}
        md={6}
        sx={{ pageBreakInside: 'avoid' }}
      >
        <CommonDrawdownChart
          data={averageDrawdown}
          chartType={DrawdownChartTypeEnum.linear}
          chartLabel={t('avgDrawdown', 'Average drawdown')}
          displayWidth={displayWidth}
          chartHeaderText={t('avgDrawdown', 'Average drawdown')}
          chartHeaderExplanation={t(
            'avgDrawdownChartDescription',
            'The chart illustrates how the average drawdown changes over the selected period of time',
          )}
          currency={currentAccount?.currency}
        />
      </Grid>

      <Grid
        item
        lg={matchesBreakpoints ? 4 : 6}
        xs={12}
        sm={12}
        md={6}
        sx={{ pageBreakInside: 'avoid' }}
      >
        <CommonDrawdownChart
          data={biggestDrawdown}
          chartType={DrawdownChartTypeEnum.linear}
          chartLabel={t('biggestDrawdown', 'Biggest drawdown')}
          displayWidth={displayWidth}
          chartHeaderText={t('biggestDrawdown', 'Biggest drawdown')}
          chartHeaderExplanation={t(
            'biggestDrawdownChartDescription',
            'The chart illustrates how the biggest drawdown changes over the selected period of time',
          )}
          currency={currentAccount?.currency}
        />
      </Grid>

      <Grid
        item
        lg={matchesBreakpoints ? 4 : 6}
        xs={12}
        sm={12}
        md={6}
        sx={{ pageBreakInside: 'avoid' }}
      >
        <CommonDrawdownChart
          data={averageNumberOfTrades}
          chartType={DrawdownChartTypeEnum.bar}
          displayWidth={displayWidth}
          chartLabel={t('tradesCount', 'Trades')}
          chartHeaderText={t('avgNumberOfTradesInDrawdown', 'Average number of trades in drawdown')}
          chartHeaderExplanation={t(
            'avgNumberOfTradesInDrawdownDescription',
            'The chart illustrates how the average number trades in drawdown changes over the selected period',
          )}
        />
      </Grid>

      <Grid
        item
        lg={matchesBreakpoints ? 4 : 6}
        xs={12}
        sm={12}
        md={6}
        sx={{ pageBreakInside: 'avoid' }}
      >
        <CommonDrawdownChart
          data={averageNumberOfDays}
          chartType={DrawdownChartTypeEnum.bar}
          displayWidth={displayWidth}
          chartLabel={t('numberOfDaysCount', 'Days')}
          chartHeaderText={t('avgNumberOfDaysInDrawdown', 'Average number of days in drawdown')}
          chartHeaderExplanation={t(
            'avgNumberOfDaysInDrawdownDescription',
            'The chart illustrates how the average number of days in drawdown changes over the selected period of time',
          )}
        />
      </Grid>

      <Grid
        item
        lg={matchesBreakpoints ? 4 : 6}
        xs={12}
        sm={12}
        md={6}
        sx={{ pageBreakInside: 'avoid' }}
      >
        <CommonDrawdownChart
          data={numberOfDays}
          chartType={DrawdownChartTypeEnum.bar}
          displayWidth={displayWidth}
          chartLabel={t('numberOfDaysCount', 'Days')}
          chartHeaderText={t('numberOfDaysInDrawdown', 'Number of days in drawdown')}
          chartHeaderExplanation={t(
            'numberOfDaysInDrawdownDescription',
            'The chart illustrates how the number of days in drawdown changes over the selected period',
          )}
        />
      </Grid>
    </Grid>
  )
}
